// ==================== RN ⇄ H5 桥接封装 ====================
// 桥由 RN 端 BridgeWebView 在页面加载前注入 window.RNBridge。
// H5 通过 invokeNative 调用原生能力（设备状态 / 开始录音 / 返回等），
// 通过 registerHandler 接收原生主动下发（如设备状态变更推送）。
// 对应 RN: src/components/webview/BridgeWebView.tsx + screens/common/webBridgeHandlers.ts

import type { PageBizParams } from '@/utils/page-context'

const H5_BRIDGE_LOG_PREFIX = '[H5Bridge]'

function h5BridgeLog(action: string, detail?: unknown) {
  if (detail === undefined) {
    console.log(H5_BRIDGE_LOG_PREFIX, action)
  } else {
    console.log(H5_BRIDGE_LOG_PREFIX, action, detail)
  }
}

function redactAuthPayload(payload: unknown): unknown {
  if (!payload || typeof payload !== 'object') return payload
  const obj = payload as Record<string, unknown>
  return {
    ...obj,
    accessToken: obj.accessToken ? '***' : undefined,
    refreshToken: obj.refreshToken ? '***' : undefined,
  }
}

export type BridgeResponse = {
  success?: boolean
  data?: unknown
  error?: string
}

export interface RNBridge {
  invokeNative: (
    method: string,
    params: unknown,
    callback?: (resp: BridgeResponse) => void,
  ) => string
  registerHandler: (name: string, fn: (params: unknown) => unknown) => void
  unregisterHandler: (name: string) => void
}

export function getBridge(): RNBridge | null {
  return (window as unknown as { RNBridge?: RNBridge }).RNBridge ?? null
}

/** 是否运行在 RN 容器内（桥可用，同步判断） */
export function isBridgeAvailable(): boolean {
  return !!getBridge()?.invokeNative
}

/**
 * 异步判断是否在 RN 容器内（桥是否最终可用）。
 * Android 的 injectedJavaScriptBeforeContentLoaded 注入时机不稳，onMounted 同步读 window.RNBridge
 * 可能尚未注入 → 误判为「浏览器/展示态」。凡是「在容器内 vs 浏览器」会切换 UI 的判断都应走本方法，
 * 等待桥注入（已注入则立即返回）。iOS 下桥在 documentStart 注入、向来同步可得，本方法亦立即返回。
 */
export async function isBridgeAvailableAsync(timeoutMs = 5000): Promise<boolean> {
  if (isBridgeAvailable()) {
    console.log('[H5Diag] isBridgeAvailableAsync: sync OK', { t: Date.now() })
    return true
  }
  const t0 = Date.now()
  const bridge = await waitForBridge(timeoutMs)
  const ok = !!bridge?.invokeNative
  console.log('[H5Diag] isBridgeAvailableAsync done', {
    elapsedMs: Date.now() - t0,
    result: ok,
    hasRNBridge: bridge ? 'object' : 'undefined',
    hasInvokeNative: typeof bridge?.invokeNative,
  })
  return ok
}

/** 等待桥注入完成（RN 在 beforeContentLoaded 注入，通常已就绪，仅做兜底轮询） */
function waitForBridge(timeoutMs = 8000): Promise<RNBridge | null> {
  const existing = getBridge()
  if (existing?.invokeNative) return Promise.resolve(existing)
  const t0 = Date.now()
  console.log('[H5Diag] waitForBridge start polling', { timeoutMs })
  return new Promise((resolve) => {
    let pollCount = 0
    const timer = window.setInterval(() => {
      pollCount++
      const bridge = getBridge()
      if (bridge?.invokeNative) {
        window.clearInterval(timer)
        console.log('[H5Diag] waitForBridge: bridge appeared', {
          elapsedMs: Date.now() - t0,
          pollCount,
        })
        resolve(bridge)
      } else if (Date.now() - t0 >= timeoutMs) {
        window.clearInterval(timer)
        const bridge = getBridge()
        console.warn('[H5Diag] waitForBridge: TIMEOUT', {
          elapsedMs: Date.now() - t0,
          pollCount,
          hasRNBridge: bridge ? 'object' : 'undefined',
          hasInvokeNative: typeof bridge?.invokeNative,
          rnBridgeKeys: bridge ? Object.keys(bridge) : null,
        })
        resolve(null)
      }
    }, 50)
  })
}

const AUTH_NATIVE_METHODS = new Set([
  'getAuthTokens',
  'refreshAuthTokens',
  'handleAuthExpired',
])

/** 原生调用默认超时:RN 回调若因桥接异常(ready 状态竞态等)迟迟不触发,避免 Promise 永久 pending */
const INVOKE_NATIVE_TIMEOUT_MS = 12000

/** 调用原生方法 */
async function invokeNative<T = unknown>(
  method: string,
  params?: unknown,
  timeoutMs = INVOKE_NATIVE_TIMEOUT_MS,
): Promise<T> {
  const bridge = await waitForBridge()
  if (!bridge?.invokeNative) {
    throw new Error('RNBridge unavailable')
  }
  if (AUTH_NATIVE_METHODS.has(method)) {
    h5BridgeLog(`invokeNative -> ${method}`, redactAuthPayload(params))
  } else {
    h5BridgeLog(`invokeNative -> ${method}`, params)
  }
  return new Promise<T>((resolve, reject) => {
    let settled = false
    const timer = window.setTimeout(() => {
      if (settled) return
      settled = true
      h5BridgeLog(`invokeNative timeout <- ${method}`, `${timeoutMs}ms`)
      reject(new Error(`Native call timeout: ${method}`))
    }, timeoutMs)
    try {
      bridge.invokeNative(method, params, (resp) => {
        if (settled) return
        settled = true
        window.clearTimeout(timer)
        if (resp?.success) {
          if (AUTH_NATIVE_METHODS.has(method)) {
            h5BridgeLog(`invokeNative ok <- ${method}`, redactAuthPayload(resp.data))
          } else {
            h5BridgeLog(`invokeNative ok <- ${method}`, resp.data)
          }
          resolve(resp.data as T)
        } else {
          h5BridgeLog(`invokeNative fail <- ${method}`, resp?.error)
          reject(new Error(resp?.error || 'Native call failed'))
        }
      })
    } catch (error) {
      if (settled) return
      settled = true
      window.clearTimeout(timer)
      h5BridgeLog(`invokeNative error <- ${method}`, error)
      reject(error)
    }
  })
}

/** 注册 H5 处理器，供原生主动下发调用 */
export function registerHandler(name: string, fn: (params: unknown) => unknown): void {
  getBridge()?.registerHandler(name, fn)
}

export function unregisterHandler(name: string): void {
  getBridge()?.unregisterHandler(name)
}

// ==================== 业务能力封装 ====================

export type DeviceStatus = 'unbound' | 'bound_connected' | 'bound_disconnected' | 'syncing'

/** 设备快照：deviceStatus + isRecording(0未录音/1录音中/2暂停中) */
export interface DeviceSnapshot {
  deviceStatus: DeviceStatus
  isRecording: number
}

/** @deprecated 使用 PageBizParams（@/utils/page-context） */
export type StrategyBizParams = PageBizParams

export interface BridgeAuthTokens {
  accessToken: string
  refreshToken?: string
  sessionInvalidated?: boolean
  businessContextSupported?: boolean
  businessContext?: unknown
}

export interface AppInfo {
  app: string
  platform: 'react-native'
  nativePlatform?: 'ios' | 'android'
  bizType?: string
  capabilities?: string[]
  /** 服务端远程开关；仅可信使用统计页由原生下发 true。 */
  durationPurchaseEnabled?: boolean
  now: string
}

/** 获取原生容器信息；nativePlatform 缺失时按旧版 App 处理，不展示原生购买入口。 */
export function getAppInfo(): Promise<AppInfo> {
  return invokeNative<AppInfo>('getAppInfo')
}

/** 获取业务参数（仅 RN，由 resolvePageContext 与浏览器 query 合并） */
export async function getBizParams(): Promise<PageBizParams> {
  try {
    return (await invokeNative<PageBizParams>('getBizParams')) || {}
  } catch {
    return {}
  }
}

/** 获取当前设备状态（初始拉取） */
export async function getDeviceStatus(): Promise<DeviceSnapshot> {
  return invokeNative<DeviceSnapshot>('getDeviceStatus')
}

/** 触发开始录音（RN 侧做配额校验 + BLE 下发 + 待归属登记） */
export interface StartRecordingResult {
  ok: boolean
  message?: string
}
export function startRecording(): Promise<StartRecordingResult> {
  return invokeNative<StartRecordingResult>('startRecording')
}

/** 返回（优先 WebView 内部回退，再退原生导航） */
export function navigateBack(from = 'consultation-strategy'): Promise<unknown> {
  return invokeNative('navigateBack', { from })
}

/** 通用跳转：H5 链接或原生路由（对应 RN webBridgeHandlers.goTo） */
export interface GoToParams {
  path: string
  params?: Record<string, unknown>
  title?: string
  showHeader?: boolean
  bizType?: string
}
export function goToNative(params: GoToParams): Promise<unknown> {
  return invokeNative('goTo', params)
}

export interface OpenAppleIAPStoreResult {
  ok: boolean
  reason?: 'unsupported-platform'
}

/** 打开原生 Apple IAP 商店；商品、价格和支付均由原生 StoreKit 处理。 */
export function openAppleIAPStore(): Promise<OpenAppleIAPStoreResult> {
  return invokeNative<OpenAppleIAPStoreResult>('openAppleIAPStore')
}

export interface OpenDurationStoreResult {
  ok: boolean
  reason?: 'unsupported-platform' | 'unsupported-context' | 'disabled'
}

/** 请求原生新开统一商店 WebView：页内支付由 iOS StoreKit / Android 微信 Bridge 执行。 */
export function openDurationStore(): Promise<OpenDurationStoreResult> {
  return invokeNative<OpenDurationStoreResult>('openDurationStore')
}

export const DURATION_STORE_CAPABILITY = 'durationStore.v1'

export type DurationStorePlatform = 'ios' | 'android'
export type DurationPaymentMethod = 'APPLE_IAP' | 'WECHAT_APP'
export type DurationPurchaseStatus =
  | 'waiting'
  | 'processing'
  | 'credited'
  | 'refunded'
  | 'closed'
  | 'cancelled'
  | 'pending'
  | 'unverified'
  | 'failed'

export interface DurationStoreProduct {
  productCode: string
  displayName: string
  displayPrice: string
  durationSeconds: number
  validDays: number | null
  purchasable: boolean
}

export interface DurationStoreCatalog {
  capability: typeof DURATION_STORE_CAPABILITY
  platform: DurationStorePlatform
  paymentMethod: DurationPaymentMethod
  validityText: string
  products: DurationStoreProduct[]
  pendingPurchase?: DurationPurchaseResult
}

export interface DurationPurchaseResult {
  status: DurationPurchaseStatus
  requestId?: string
  message?: string
}

/** 目录来自原生：iOS 合并服务端白名单与 StoreKit，Android 读取服务端统一商品配置。 */
export function getDurationStoreCatalog(forceRefresh = false): Promise<DurationStoreCatalog> {
  return invokeNative<DurationStoreCatalog>('getDurationStoreCatalog', { forceRefresh }, 30000)
}

/** H5 只提交统一 productCode；价格、权益、用户和渠道内部 ID 均由原生/服务端重取。 */
export function purchaseDurationProduct(productCode: string): Promise<DurationPurchaseResult> {
  return invokeNative<DurationPurchaseResult>(
    'purchaseDurationProduct',
    { productCode },
    120000,
  )
}

export function queryDurationPurchase(requestId: string): Promise<DurationPurchaseResult> {
  return invokeNative<DurationPurchaseResult>(
    'queryDurationPurchase',
    { requestId },
    30000,
  )
}

export const DURATION_PURCHASE_HISTORY_CAPABILITY = 'durationPurchaseHistory.v1'

export type DurationHistoryStatus =
  | 'waiting'
  | 'processing'
  | 'credited'
  | 'suppressed'
  | 'failed'
  | 'closed'
  | 'refunded'

export type DurationHistoryRefundStatus =
  | 'none'
  | 'requested'
  | 'declined'
  | 'refunded'
  | 'refund_reversed'
  | 'unknown'

export type DurationHistoryRecoveryStatus =
  | 'not_applicable'
  | 'full'
  | 'partial'
  | 'none_available'
  | 'unknown'

/** 已发布 App 可能透传毫秒时间戳，新版 Bridge 会统一返回 ISO 字符串。 */
export type DurationHistoryTime = string | number

export interface DurationPurchaseHistoryItem {
  purchaseId: string
  platform: DurationStorePlatform
  paymentMethod: DurationPaymentMethod
  productName: string
  displayPrice?: string
  durationSeconds: number
  validDays?: number | null
  status: DurationHistoryStatus
  refundStatus: DurationHistoryRefundStatus
  purchaseTime?: DurationHistoryTime | null
  creditedTime?: DurationHistoryTime | null
}

export interface DurationPurchaseHistorySnapshot {
  capability: typeof DURATION_PURCHASE_HISTORY_CAPABILITY
  platform: DurationStorePlatform
  items: DurationPurchaseHistoryItem[]
}

export interface DurationPurchaseHistoryDetail extends DurationPurchaseHistoryItem {
  payTime?: DurationHistoryTime | null
  expireTime?: DurationHistoryTime | null
  recoveryStatus: DurationHistoryRecoveryStatus
  canRequestRefund: boolean
  officialRefundAvailable: boolean
}

export type DurationRefundRequestStatus =
  | 'submitted'
  | 'duplicateRequest'
  | 'userCancelled'
  | 'unsupported'
  | 'failed'

export interface DurationRefundRequestResult {
  status: DurationRefundRequestStatus
  transactionId: string
  reasonCode?: string
}

export function getDurationPurchaseHistory(
  forceRefresh = false,
): Promise<DurationPurchaseHistorySnapshot> {
  return invokeNative<DurationPurchaseHistorySnapshot>(
    'getDurationPurchaseHistory',
    { forceRefresh },
    30000,
  )
}

export function getDurationPurchaseHistoryDetail(
  purchaseId: string,
  forceRefresh = false,
): Promise<DurationPurchaseHistoryDetail> {
  return invokeNative<DurationPurchaseHistoryDetail>(
    'getDurationPurchaseHistoryDetail',
    { purchaseId, forceRefresh },
    30000,
  )
}

export function requestDurationPurchaseRefund(
  purchaseId: string,
): Promise<DurationRefundRequestResult> {
  return invokeNative<DurationRefundRequestResult>(
    'requestDurationPurchaseRefund',
    { purchaseId },
    120000,
  )
}

export function openAppleOfficialRefundPage(): Promise<{ ok: true }> {
  return invokeNative<{ ok: true }>('openAppleOfficialRefundPage')
}

// ==================== 录音详情：分享 / 列表刷新 ====================

export type WechatShareScene = 'session' | 'timeline'

export interface ShareToWechatParams {
  scene: WechatShareScene
  title: string
  description?: string
  webpageUrl: string
  thumbImageUrl?: string
}

export interface ShareToWechatResult {
  ok: boolean
}

/**
 * 调起原生微信分享（好友 / 朋友圈）。
 * 原生侧复用 utils/wechat.ts 的 shareWebpageToSession / shareWebpageToTimeline。
 * 未装微信 / 用户取消时，原生返回 success:false，H5 自行降级。
 */
export function shareToWechat(params: ShareToWechatParams): Promise<ShareToWechatResult> {
  return invokeNative<ShareToWechatResult>('shareToWechat', params)
}

/** 运营动作成功后通知原生列表刷新（确认归属 / 标无效 / 改时长等） */
export type ListRefreshReason = 'matched' | 'markedInvalid' | 'durationUpdated'

export function notifyListRefresh(reason: ListRefreshReason, recordingId?: string): void {
  if (!isBridgeAvailable()) return
  void invokeNative('notifyListRefresh', { reason, recordingId }).catch(() => {
    // 通知失败不影响 H5 主流程
  })
}

/**
 * 监听「页面重新展示」：原生 WebView 被其他原生页（如归属/修改客户页）覆盖后又回到前台时，
 * 由原生通过 callH5('onPageShow') 主动下发。用于返回后刷新当前页数据。
 * 返回取消监听函数。
 */
export function onPageShow(cb: () => void): () => void {
  const handler = (params: unknown) => {
    h5BridgeLog('receive push <- onPageShow', params)
    cb()
    return { received: true }
  }
  registerHandler('onPageShow', handler)
  return () => unregisterHandler('onPageShow')
}

const deviceStatusListeners = new Set<(snapshot: DeviceSnapshot) => void>()
let deviceStatusHandlerInstalled = false

/** 尽早注册，避免 RN 在页面 onMounted 前 push 导致 handler not found */
export function installDeviceStatusBridgeHandler(): void {
  if (deviceStatusHandlerInstalled) return
  const bridge = getBridge()
  if (!bridge?.registerHandler) return
  deviceStatusHandlerInstalled = true
  registerHandler('onDeviceStatusChange', (params: unknown) => {
    const snapshot = params as DeviceSnapshot
    h5BridgeLog('receive push <- onDeviceStatusChange', snapshot)
    deviceStatusListeners.forEach((cb) => cb(snapshot))
    return { received: true }
  })
  h5BridgeLog('registerHandler onDeviceStatusChange (global)')
}

/** 监听设备状态变更推送，返回取消监听函数 */
export function onDeviceStatusChange(cb: (snapshot: DeviceSnapshot) => void): () => void {
  installDeviceStatusBridgeHandler()
  deviceStatusListeners.add(cb)
  return () => deviceStatusListeners.delete(cb)
}

if (typeof window !== 'undefined') {
  void waitForBridge().then((bridge) => {
    if (bridge) installDeviceStatusBridgeHandler()
  })
}

// ==================== 认证能力封装 ====================

/** 获取当前 token（优先走 RN） */
export async function getAuthTokensFromNative(timeoutMs?: number): Promise<BridgeAuthTokens | null> {
  if (!isBridgeAvailable()) return null
  try {
    const data = await invokeNative<BridgeAuthTokens>('getAuthTokens', undefined, timeoutMs)
    if (!data?.accessToken) return null
    return data
  } catch {
    return null
  }
}

/** 401 后请求 RN 刷新 token */
export async function refreshAuthTokensFromNative(): Promise<BridgeAuthTokens | null> {
  if (!isBridgeAvailable()) return null
  try {
    const data = await invokeNative<BridgeAuthTokens>('refreshAuthTokens')
    if (!data?.accessToken) return null
    return data
  } catch {
    return null
  }
}

/** 通知 RN：登录已失效，要求跳登录页 */
export async function notifyNativeAuthExpired(): Promise<void> {
  if (!isBridgeAvailable()) return
  try {
    await invokeNative('handleAuthExpired', { from: 'h5' })
  } catch {
    // ignore
  }
}

/** 监听 RN 主动推送的新 token（刷新成功后可选下发） */
export function onAuthTokensUpdated(
  cb: (tokens: BridgeAuthTokens) => void,
): () => void {
  const handler = (params: unknown) => {
    h5BridgeLog('receive push <- onAuthTokensUpdated', redactAuthPayload(params))
    cb(params as BridgeAuthTokens)
    return { received: true }
  }
  registerHandler('onAuthTokensUpdated', handler)
  h5BridgeLog('registerHandler onAuthTokensUpdated')
  return () => unregisterHandler('onAuthTokensUpdated')
}

/** 仅在新 App 声明支持时使用，避免旧桥等待未知方法。 */
export function getBusinessContextFromNative(forceRefresh = false): Promise<unknown> {
  return invokeNative('getBusinessContext', { forceRefresh })
}
